import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useOrganizationStore } from '@/core/stores/organization.store';
import { boardStatusesService } from '../api/board-statuses.service';
import { BOARD_KEYS } from './queryKeys';
import type { CreateBoardStatusPayload, UpdateBoardStatusPayload, ReorderPayload } from '../types';

export function useCreateBoardStatus(boardId: string) {
  const queryClient = useQueryClient();
  const orgId = useOrganizationStore((s) => s.activeOrganizationId);

  return useMutation({
    mutationFn: (data: CreateBoardStatusPayload) => boardStatusesService.create(boardId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: BOARD_KEYS.statuses(orgId ?? '', boardId),
      });
    },
  });
}

export function useUpdateBoardStatus(boardId: string) {
  const queryClient = useQueryClient();
  const orgId = useOrganizationStore((s) => s.activeOrganizationId);

  return useMutation({
    mutationFn: ({ statusId, data }: { statusId: string; data: UpdateBoardStatusPayload }) =>
      boardStatusesService.update(boardId, statusId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: BOARD_KEYS.statuses(orgId ?? '', boardId),
      });
    },
  });
}

export function useDeleteBoardStatus(boardId: string) {
  const queryClient = useQueryClient();
  const orgId = useOrganizationStore((s) => s.activeOrganizationId);

  return useMutation({
    mutationFn: (statusId: string) => boardStatusesService.delete(boardId, statusId),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: BOARD_KEYS.statuses(orgId ?? '', boardId),
      });
    },
  });
}

export function useReorderBoardStatuses(boardId: string) {
  const queryClient = useQueryClient();
  const orgId = useOrganizationStore((s) => s.activeOrganizationId);

  return useMutation({
    mutationFn: (data: ReorderPayload) => boardStatusesService.reorder(boardId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: BOARD_KEYS.statuses(orgId ?? '', boardId),
      });
    },
  });
}
